import { useRef, useCallback, useMemo, useState, useEffect } from "react";
import { Box, Paper, Typography, Stack, alpha, IconButton, Tooltip } from "@mui/material";
import FlightIcon from "@mui/icons-material/Flight";
import HomeIcon from "@mui/icons-material/Home";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { MapContainer, StatsPanel, AlertsPanel } from "../components";
import type { MapContainerRef } from "../components";
import { generateAlerts, type Alert } from "../data/pipelineData";
import { useTheme } from "../../../shared/context/ThemeContext";

export const Geospatial = () => {
  const { mode } = useTheme();
  const mapRef = useRef<MapContainerRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const alerts = useMemo(() => generateAlerts(), []);
  const [isDroneMode, setIsDroneMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nodesVisible, setNodesVisible] = useState(true);

  const handleAlertClick = useCallback((alert: Alert) => {
    mapRef.current?.flyToAlert(alert);
  }, []);

  const handleDroneToggle = useCallback(() => {
    if (isDroneMode) {
      mapRef.current?.resetView();
      setIsDroneMode(false);
    } else {
      mapRef.current?.startDroneAnimation();
      setIsDroneMode(true);
    }
  }, [isDroneMode]);

  const handleResetView = useCallback(() => {
    mapRef.current?.resetView();
    setIsDroneMode(false);
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleNodesToggle = useCallback(() => {
    const newValue = !nodesVisible;
    mapRef.current?.setNodesVisible(newValue);
    setNodesVisible(newValue);
  }, [nodesVisible]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  return (
    <Box ref={containerRef} sx={{ height: "100%", position: "relative", overflow: "hidden", bgcolor: "background.default" }}>
      <MapContainer themeMode={mode} ref={mapRef} />
      <StatsPanel />

      {/* Alerts Panel - Right Side */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 10,
        }}
      >
        <AlertsPanel
          alerts={alerts}
          onAlertClick={handleAlertClick}
        />
      </Box>

      {/* Drone/Reset Controls - Bottom Right */}
      <Box
        sx={{
          position: "absolute",
          bottom: 80,
          right: 16,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Tooltip title={isDroneMode ? "Stop Drone View" : "Start Drone View"} placement="left">
          <Paper
            elevation={0}
            sx={{
              bgcolor: (theme) =>
                isDroneMode
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
              backdropFilter: "blur(12px)",
              border: "1px solid",
              borderColor: isDroneMode ? "primary.main" : "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <IconButton
              onClick={handleDroneToggle}
              sx={{
                color: isDroneMode ? "primary.contrastText" : "text.primary",
                "&:hover": {
                  bgcolor: isDroneMode ? "primary.dark" : "action.hover",
                },
              }}
            >
              <FlightIcon
                sx={{
                  transform: isDroneMode ? "rotate(45deg)" : "none",
                  transition: "transform 0.3s ease",
                }}
              />
            </IconButton>
          </Paper>
        </Tooltip>

        <Tooltip title="Reset View" placement="left">
          <Paper
            elevation={0}
            sx={{
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
              backdropFilter: "blur(12px)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <IconButton onClick={handleResetView} sx={{ color: "text.primary" }}>
              <HomeIcon />
            </IconButton>
          </Paper>
        </Tooltip>

        <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} placement="left">
          <Paper
            elevation={0}
            sx={{
              bgcolor: (theme) =>
                isFullscreen
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
              backdropFilter: "blur(12px)",
              border: "1px solid",
              borderColor: isFullscreen ? "primary.main" : "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <IconButton
              onClick={handleFullscreenToggle}
              sx={{
                color: isFullscreen ? "primary.contrastText" : "text.primary",
                "&:hover": {
                  bgcolor: isFullscreen ? "primary.dark" : "action.hover",
                },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Paper>
        </Tooltip>

        <Tooltip title={nodesVisible ? "Hide Nodes" : "Show Nodes"} placement="left">
          <Paper
            elevation={0}
            sx={{
              bgcolor: (theme) =>
                !nodesVisible
                  ? alpha(theme.palette.warning.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
              backdropFilter: "blur(12px)",
              border: "1px solid",
              borderColor: !nodesVisible ? "warning.main" : "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <IconButton
              onClick={handleNodesToggle}
              sx={{
                color: !nodesVisible ? "warning.contrastText" : "text.primary",
                "&:hover": {
                  bgcolor: !nodesVisible ? "warning.dark" : "action.hover",
                },
              }}
            >
              {nodesVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Paper>
        </Tooltip>
      </Box>

      {/* Bottom Status Bar */}
      <Paper
        elevation={0}
        sx={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          px: 3,
          py: 1,
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
          backdropFilter: "blur(12px)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "success.main" }} />
            <Typography variant="caption">12,847 Online</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "warning.main" }} />
            <Typography variant="caption">153 Degraded</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "error.main" }} />
            <Typography variant="caption">0 Offline</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};
